import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CertificateFormData } from '../types/certificate'
import './CertificateForm.css'

const certificateSchema = z.object({
  type: z.enum(['PCRS', 'Healthlink']),
  name: z.string().min(1, 'Name is required').min(3, 'Name must be at least 3 characters'),
  description: z.string().optional(),
  password: z.string().optional(),
  notes: z.string().optional(),
}).refine(
  (data) => {
    if (!data.password) {
      return false
    }
    return true
  },
  {
    message: 'Password is required for PCRS and Healthlink certificates',
    path: ['password'],
  }
)

interface CertificateFormProps {
  initialData?: Partial<CertificateFormData>
  onSubmit: (data: CertificateFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function CertificateForm({ initialData, onSubmit, onCancel, isLoading }: CertificateFormProps) {
  const [certificateFile, setCertificateFile] = useState<File | null>(null)
  const [privateKeyFile, setPrivateKeyFile] = useState<File | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CertificateFormData>({
    resolver: zodResolver(certificateSchema),
    defaultValues: initialData,
  })

  const certificateType = watch('type')

  const handleFormSubmit = async (data: CertificateFormData) => {
    const formData: CertificateFormData = {
      ...data,
      certificateFile: certificateFile || undefined,
      privateKeyFile: privateKeyFile || undefined,
    }
    await onSubmit(formData)
  }

  const handleCertificateFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCertificateFile(e.target.files[0])
    }
  }

  const handlePrivateKeyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPrivateKeyFile(e.target.files[0])
    }
  }

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="certificate-form"
      noValidate
      aria-label="Certificate upload form"
    >
      <div className="form-section">
        <h2>Certificate Information</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="type">
              Certificate Type <span className="required" aria-label="Required">*</span>
            </label>
            <select
              id="type"
              {...register('type')}
              className={errors.type ? 'error' : ''}
              aria-required="true"
              aria-invalid={!!errors.type}
            >
              <option value="PCRS">PCRS (Primary Care Reimbursement Service)</option>
              <option value="Healthlink">Healthlink</option>
            </select>
            {errors.type && (
              <span className="error-message" role="alert">
                {errors.type.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="name">
              Certificate Name <span className="required" aria-label="Required">*</span>
            </label>
            <input
              id="name"
              type="text"
              {...register('name')}
              className={errors.name ? 'error' : ''}
              aria-required="true"
              aria-invalid={!!errors.name}
              placeholder="e.g., PCRS Production Certificate"
            />
            {errors.name && (
              <span className="error-message" role="alert">
                {errors.name.message}
              </span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            {...register('description')}
            rows={2}
            placeholder="Optional description of this certificate"
          />
        </div>
      </div>

      <div className="form-section">
        <h2>Certificate Files</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="certificateFile">
              Certificate File (.p12, .pfx) <span className="required" aria-label="Required">*</span>
            </label>
            <input
              id="certificateFile"
              type="file"
              accept=".p12,.pfx"
              onChange={handleCertificateFileChange}
              aria-required="true"
              aria-describedby="certificateFile-hint"
            />
            <span id="certificateFile-hint" className="field-hint">
              Upload your PKCS#12 certificate file. Supported formats: .p12, .pfx
            </span>
            {certificateFile && (
              <div className="file-info">
                <span className="file-name">✓ {certificateFile.name}</span>
                <span className="file-size">({(certificateFile.size / 1024).toFixed(2)} KB)</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="privateKeyFile">Private Key File (Optional)</label>
            <input
              id="privateKeyFile"
              type="file"
              accept=".key,.pem"
              onChange={handlePrivateKeyFileChange}
              aria-describedby="privateKeyFile-hint"
            />
            <span id="privateKeyFile-hint" className="field-hint">
              Upload a private key only if provided separately by the issuer
            </span>
            {privateKeyFile && (
              <div className="file-info">
                <span className="file-name">✓ {privateKeyFile.name}</span>
                <span className="file-size">({(privateKeyFile.size / 1024).toFixed(2)} KB)</span>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="password">
            Certificate Password <span className="required" aria-label="Required">*</span>
          </label>
          <input
            id="password"
            type="password"
            {...register('password')}
            className={errors.password ? 'error' : ''}
            aria-required="true"
            aria-invalid={!!errors.password}
            aria-describedby="password-hint"
            autoComplete="off"
          />
          <span id="password-hint" className="field-hint">
            Password required for PCRS and Healthlink certificates
          </span>
          {errors.password && (
            <span className="error-message" role="alert">
              {errors.password.message}
            </span>
          )}
        </div>
      </div>

      <div className="form-section">
        <h2>Additional Information</h2>
        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            {...register('notes')}
            rows={3}
            placeholder="Additional notes about this certificate"
          />
        </div>
      </div>

      {certificateType === 'PCRS' && (
        <div className="info-box">
          <h3>PCRS Certificate Information</h3>
          <p>
            PCRS certificates are used for secure communication with the Primary Care Reimbursement Service.
            Ensure your certificate is valid and not expired before uploading.
          </p>
        </div>
      )}

      {certificateType === 'Healthlink' && (
        <div className="info-box">
          <h3>Healthlink Certificate Information</h3>
          <p>
            Healthlink certificates require a password for secure access. Make sure you have the correct
            password before uploading. Certificates are encrypted and stored securely.
          </p>
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isLoading}
          aria-label="Cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading || !certificateFile}
          aria-label="Upload Certificate"
        >
          {isLoading ? 'Uploading...' : 'Upload Certificate'}
        </button>
      </div>
    </form>
  )
}



































